
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ShoppingCart, Loader2 } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";
import { trackEvent } from "@/lib/tracking";

// LIVE ADD TO CART
interface LiveAddToCartProps {
    data: {
        text?: string;
        backgroundColor?: string;
        color?: string;
        productId?: string;
        productName?: string;
        productPrice?: number;
        productImage?: string;
    };
    onNext?: () => void;
}

export const LiveAddToCart = ({ data, onNext }: LiveAddToCartProps) => {
    const { addToCart, openCart } = useCart();
    const [loading, setLoading] = useState(false);

    const handleClick = async () => {
        if (!data.productId) {
            toast.error("لم يتم تحديد منتج لهذا الزر يجب إعداده من لوحة التحكم");
            return;
        }

        setLoading(true);
        try {
            // Ideally we fetch latest price, but for now use cached or settings
            addToCart({
                productId: data.productId,
                name: data.productName || "Product",
                price: data.productPrice || 0,
                quantity: 1,
                image: data.productImage || ""
            });

            trackEvent("AddToCart", {
                content_name: data.productName,
                content_id: data.productId,
                value: data.productPrice,
                currency: "EGP"
            });

            toast.success("تمت الإضافة للسلة");

            // Wait a bit then go next or open cart
            setTimeout(() => {
                if (onNext) {
                    onNext();
                } else {
                    openCart();
                }
                setLoading(false);
            }, 500);

        } catch (error) {
            console.error(error);
            toast.error("حدث خطأ ما");
            setLoading(false);
        }
    };

    return (
        <Button
            className="w-full gap-2 transition-all active:scale-95"
            onClick={handleClick}
            disabled={loading}
            style={{
                backgroundColor: data.backgroundColor || "#000000",
                color: data.color || "#ffffff"
            }}
        >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />}
            {data.text || "أضف للسلة"}
        </Button>
    );
};

// LIVE CHECKOUT FORM
interface LiveCheckoutFormProps {
    data: any;
    onNext?: () => void;
    funnelId?: string;
    stepId?: string;
}

export const LiveCheckoutForm = ({ data, onNext, funnelId, stepId }: LiveCheckoutFormProps) => {
    const { items: cart, total, clearCart } = useCart();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        phone: "",
        address: "",
        city: "",
        paymentMethod: "cod"
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            // Create Order
            const orderData = {
                customer: {
                    name: formData.name,
                    phone: formData.phone,
                    address: formData.address,
                    city: formData.city,
                },
                items: cart,
                total_price: total,
                payment_method: formData.paymentMethod,
                funnel_id: funnelId,
                step_id: stepId,
                status: "pending"
            };

            const res = await fetch("/api/orders", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(orderData)
            });

            const json = await res.json();

            if (!res.ok) throw new Error(json.error?.message || "Order failed");

            // Track Purchase
            trackEvent("Purchase", {
                transaction_id: json.data.id,
                value: total,
                currency: "EGP",
                items: cart
            });

            toast.success("تم استلام طلبك بنجاح!");
            clearCart();

            if (onNext) {
                onNext();
            }
        } catch (error: any) {
            console.error(error);
            toast.error(error.message || "حدث خطأ أثناء الدفع");
        } finally {
            setLoading(false);
        }
    };

    if (cart.length === 0) {
        return (
            <div className="p-8 text-center border rounded-lg bg-gray-50">
                <p className="text-gray-500 mb-4">السلة فارغة</p>
                <Button variant="outline" onClick={() => window.history.back()}>العودة للتسوق</Button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-xl font-bold mb-4">إتمام الطلب</h3>

            <div className="space-y-4">
                <div>
                    <Label htmlFor="name">الاسم بالكامل</Label>
                    <Input
                        id="name"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="الاسم الثلاثي"
                    />
                </div>

                <div>
                    <Label htmlFor="phone">رقم الهاتف</Label>
                    <Input
                        id="phone"
                        required
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        placeholder="01xxxxxxxxx"
                    />
                </div>

                <div>
                    <Label htmlFor="city">المحافظة / المدينة</Label>
                    <Input
                        id="city"
                        required
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        placeholder="القاهرة"
                    />
                </div>

                <div>
                    <Label htmlFor="address">العنوان بالتفصيل</Label>
                    <Input
                        id="address"
                        required
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="اسم الشارع، رقم العمارة"
                    />
                </div>
            </div>

            <div className="space-y-4 pt-4 border-t">
                <Label>طريقة الدفع</Label>
                <RadioGroup
                    value={formData.paymentMethod}
                    onValueChange={(v) => setFormData({ ...formData, paymentMethod: v })}
                >
                    <div className="flex items-center space-x-2 space-x-reverse">
                        <RadioGroupItem value="cod" id="cod" />
                        <Label htmlFor="cod">الدفع عند الاستلام</Label>
                    </div>
                </RadioGroup>
            </div>

            <div className="pt-4 border-t space-y-2">
                <div className="flex justify-between font-bold text-lg">
                    <span>الإجمالي</span>
                    <span>{total} EGP</span>
                </div>
            </div>

            <Button type="submit" className="w-full gap-2" size="lg" disabled={loading}>
                {loading && <Loader2 className="animate-spin" />}
                تأكيد الطلب
            </Button>
        </form>
    );
};
